import React from "react";

// Detects the "stale chunk after deploy" failure. When a new version ships,
// old hashed route chunks are removed from the server; a browser still holding
// the previous main bundle requests a now-missing chunk and the dynamic
// import() rejects with a ChunkLoadError. Without a boundary this unmounts the
// whole React tree and the user sees a blank white page.
function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || "";
  const msg = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg)
  );
}

const RELOAD_GUARD_KEY = "hg_chunk_reload_at";

// Best-effort remote report so a production-only crash we cannot reproduce
// locally still leaves a breadcrumb on the server.
function reportError(error, info) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const payload = {
      message: (error && (error.message || String(error))) || "unknown",
      stack: (error && error.stack ? String(error.stack) : "").slice(0, 4000),
      component_stack: (info && info.componentStack ? String(info.componentStack) : "").slice(0, 4000),
      route: typeof window !== "undefined" ? window.location.pathname : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    };
    // fire-and-forget; keepalive lets it survive an imminent reload
    fetch(`${base}/api/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      keepalive: true,
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch (e) {
    /* never let reporting throw */
  }
}

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, chunkError: false, message: "", details: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      chunkError: isChunkLoadError(error),
      message: (error && (error.message || String(error))) || "Unknown error",
    };
  }

  componentDidCatch(error, info) {
    const details =
      ((error && error.stack) || "") +
      "\n\n--- component stack ---" +
      ((info && info.componentStack) || "");
    this.setState({ details });
    reportError(error, info);

    if (isChunkLoadError(error)) {
      // Auto-recover from a stale-chunk deploy by reloading ONCE. A
      // sessionStorage guard prevents an infinite reload loop if the fresh
      // load somehow fails again for the same reason.
      let last = 0;
      try {
        last = parseInt(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || "0", 10);
      } catch (e) {
        last = 0;
      }
      const now = Date.now();
      if (now - last > 15000) {
        try {
          window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
        } catch (e) {
          /* ignore private-mode storage errors */
        }
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    try {
      window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch (e) {
      /* ignore */
    }
    window.location.reload();
  };

  handleCopy = () => {
    try {
      navigator.clipboard.writeText(
        `${this.state.message}\n\n${this.state.details}`
      );
    } catch (e) {
      /* ignore */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // A chunk error is mid auto-reload — keep the screen calm, not blank.
    if (this.state.chunkError) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-4 text-center px-6">
            <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
            <p className="text-sm text-muted-foreground">
              Updating to the latest version…
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="text-sm px-4 py-2 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-500"
              data-testid="chunk-error-reload-btn"
            >
              Reload now
            </button>
          </div>
        </div>
      );
    }

    // Any other render error: surface the message on screen (never a blank
    // page) so it can be diagnosed even in production.
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="w-full max-w-2xl bg-white border border-red-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">
            Something went wrong on this page
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Please reload to continue. If it keeps happening, copy the details
            below and send them to support.
          </p>
          <div
            className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-xs font-mono text-red-800 break-words"
            data-testid="page-error-message"
          >
            {this.state.message}
          </div>
          {this.state.details && (
            <details className="mt-2">
              <summary className="text-xs text-slate-500 cursor-pointer">
                Technical details
              </summary>
              <pre className="mt-2 max-h-56 overflow-auto bg-slate-50 border border-slate-200 rounded-md p-3 text-[11px] text-slate-700 whitespace-pre-wrap">
                {this.state.details}
              </pre>
            </details>
          )}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="text-sm px-4 py-2 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-500"
              data-testid="page-error-reload-btn"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleCopy}
              className="text-sm px-4 py-2 rounded-md bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="page-error-copy-btn"
            >
              Copy details
            </button>
          </div>
        </div>
      </div>
    );
  }
}
